import type { DragEndEvent, DragMoveEvent, DragOverEvent } from "@dnd-kit/core"
import { arrayMove } from "@dnd-kit/sortable"

export type DragEvent = DragEndEvent | DragMoveEvent | DragOverEvent

type MoveConfig<Item> = {
  fromDroppable?: string
  toDroppable?: string
  fromIndex?: number
  draggableId?: string
  toIndex?: number
  item?: Item
}

export type MoveResult<Item> = {
  didMove: boolean
} & MoveConfig<Item>

type AnySetter<Item> =
  | {
      statefulSetter: (
        cb: <ItemType extends Item>(prevState: ItemType[]) => ItemType[],
        result: MoveConfig<Item>,
      ) => void
    }
  | {
      directSetter: <ItemType extends Item>(items: ItemType[], result: MoveConfig<Item>) => void
    }

/**
 * Moveable is a type that represents a collection of items that can be moved.
 * Setting the items can be done either through a stateful setter that provides it's own previous state (like useState) or a direct setter just takes a new array (this internally falls back to the state of the supplied `items` in the collection).
 */
type Moveable<Item> = {
  items: Item[]
} & AnySetter<Item>

/**
 * Maps a collection of items to a map of moveables. This is useful for when you have a dynamic collection of items in which items can be moved between different droppable areas.
 * Example use case would be the project board where you have multiple lanes and cards that can be moved between them.
 *
 * @param collections
 * @param extractor
 * @returns
 */
export const mapMoveables = <DroppableList, Item>(
  collections: DroppableList[],
  extractor: (
    item: DroppableList,
    index: number,
  ) => {
    droppableId: string
  } & Moveable<Item>,
): Record<string, Moveable<Item>> => {
  const map = {}
  collections.forEach((collection, i) => {
    const { droppableId: id, ...rest } = extractor(collection, i)
    map[id] = rest
  })
  return map
}

type MoveItemOptions<Item> = {
  canMoveItem?: (props: {
    item: Item
    fromDroppable: string
    toDroppable: string
    fromIndex: number
    toIndex: number
  }) => boolean
  restrictMovesTo?: "all" | "sameDroppableOnly" | "differentDroppableOnly"
}

/**
 * When moving items between droppables this throttle is useful to prevent rapid back-and-forth updates when an item is moved precisely between two droppables. For any other kind of move this is not needed.
 */
const DROPPABLE_THROTTLE_TIMEOUT = 200 // milliseconds
const GLOBAL_DROPPABLE_THROTTLE_MAP = {}

/**
 * Moves an item between droppables.
 * @param moveEvent The drag event containing information about the item being moved.
 * @param droppables The droppable areas in the application.
 * @param options Options for the move operation.
 * @param options.canMoveItem A function that determines if the item can be moved.
 * @param options.restrictMovesTo Restricts the moves to a specific droppable.
 *   - "all": Default - No restrictions.
 *   - "sameDroppableOnly": Only allow moves within the same droppable.
 *   - "differentDroppableOnly": Only allow moves to a different droppable. This is useful for "onDragOver" operation where the item has to animate to the new droppable.
 * @returns The result of the move operation.
 */
export const moveItem = <Item,>(
  moveEvent: DragEvent,
  droppables: Record<string, Moveable<Item>>,
  options?: MoveItemOptions<Item>,
): MoveResult<Item> => {
  const { active, over } = moveEvent

  if (!active || !over) {
    return { didMove: false }
  }
  const fromDroppableId = active.data.current.sortable.containerId
  const toDroppableId = over.data.current.sortable.containerId
  const fromIndex = active.data.current.sortable.index as number
  const toIndex = over.data.current.sortable.index as number

  if (!fromDroppableId) {
    throw new Error("Item in active state has no containerId")
  }
  if (!toDroppableId) {
    throw new Error("Item in over state has no containerId")
  }

  const result: MoveResult<Item> = {
    didMove: false,
    fromDroppable: fromDroppableId,
    toDroppable: toDroppableId,
    draggableId: active.id as string,
    fromIndex,
    toIndex,
  }

  const fromDroppable = droppables[fromDroppableId]
  if (!fromDroppable) {
    return result
  }

  const toDroppable = droppables[toDroppableId]
  if (!toDroppable) {
    if (!toDroppable) {
      return result
    }
  }

  const activeItem = fromDroppable.items[fromIndex]
  if (!activeItem) {
    return result
  }
  if (options?.canMoveItem) {
    if (
      !options.canMoveItem({
        item: activeItem,
        fromDroppable: fromDroppableId,
        toDroppable: toDroppableId,
        fromIndex,
        toIndex,
      })
    ) {
      return result
    }
  }

  const insideSameDroppable = fromDroppableId === toDroppableId
  const restrictMovesTo = options?.restrictMovesTo ?? "all"

  /**
   * The item should not be moved if:
   * - The item is moved within the same droppable and the index didn't change
   * - The item is moved within the same droppable and the `restrictMovesTo` option is set to "differentDroppableOnly"
   * - The item is moved to a different droppable and the `restrictMovesTo` option is set to "sameDroppableOnly"
   *  */
  if (
    (insideSameDroppable && (fromIndex === toIndex || restrictMovesTo === "differentDroppableOnly")) ||
    (!insideSameDroppable && restrictMovesTo === "sameDroppableOnly")
  ) {
    return result
  }

  // If the item is moved to the same droppable, we can just move it in place
  if (insideSameDroppable) {
    result.didMove = true
    if ("directSetter" in fromDroppable) {
      fromDroppable.directSetter(arrayMove(fromDroppable.items, fromIndex, toIndex), result)
    } else {
      fromDroppable.statefulSetter((prev) => arrayMove(prev, fromIndex, toIndex), result)
    }
  } else {
    const cooldownFromId = `${fromDroppableId}-${toDroppableId}-${active.id}`
    if (GLOBAL_DROPPABLE_THROTTLE_MAP[cooldownFromId]) {
      return result
    }

    result.didMove = true

    if ("directSetter" in fromDroppable) {
      const prev = fromDroppable.items
      const copy = [...prev]
      copy.splice(fromIndex, 1)
      fromDroppable.directSetter(copy, result)
    } else {
      fromDroppable.statefulSetter((prev) => {
        const copy = [...prev]
        copy.splice(fromIndex, 1)
        return copy
      }, result)
    }

    if ("directSetter" in toDroppable) {
      const prevDest = toDroppable.items
      const copyDest = [...prevDest]
      copyDest.splice(toIndex, 0, activeItem)
      toDroppable.directSetter(copyDest, result)
    } else {
      toDroppable.statefulSetter((prev) => {
        const copy = [...prev]
        copy.splice(toIndex, 0, activeItem as (typeof copy)[number])
        return copy
      }, result)
    }

    const cooldownToId = `${toDroppableId}-${fromDroppableId}-${active.id}`
    GLOBAL_DROPPABLE_THROTTLE_MAP[cooldownToId] = true
    setTimeout(() => {
      if (GLOBAL_DROPPABLE_THROTTLE_MAP[cooldownToId]) {
        delete GLOBAL_DROPPABLE_THROTTLE_MAP[cooldownToId]
      }
    }, DROPPABLE_THROTTLE_TIMEOUT)
  }

  return result
}
